import React, { useState, useEffect, useMemo } from 'react';
import {
  FormControlLabel,
  FormControl,
  Grid,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Backdrop,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Iconify from '../iconify';
import { toast } from 'react-toastify';
import api from 'src/services/BaseService';

const DeliveryModal = ({
  open,
  count,
  packageCount,
  onClose,
  onBack,
  onDeliver,
  onComplete,
  onPorterDone,
  selectedItems,
  onAddPersonel,
  isLoading,
  showPorterOption = false,
  muhaberatStaticQR = '',
  onCancelPorter,
}) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [lastname, setLastname] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [code, setCode] = useState('');
  const [selectedOption, setSelectedOption] = useState('manuel');
  const [selectedPersonel, setSelectedPersonel] = useState('');
  const [personelList, setPersonelList] = useState([]);
  const [isBackdropOpen, setIsBackdropOpen] = useState(false);
  const [withoutTowerExist, setWithoutTowerExist] = useState(false);
  const [porterList, setPorterList] = useState([]);
  const [selectedPorter, setSelectedPorter] = useState('');

  const isPorterFlow = selectedOption === 'porter';

  useEffect(() => {
    if (!showPorterOption && selectedOption === 'porter') {
      setSelectedOption('manuel');
      setSelectedPorter('');
      setName('');
      setLastname('');
      setPhone('');
      setNote('');
    }
  }, [showPorterOption, selectedOption]);

  useEffect(() => {
    let hasEmptyTower = false;

    for (let i = 0; i < selectedItems.length; i++) {
      if (!selectedItems[i]?.dataJSON?.tower) {
        hasEmptyTower = true;
        break;
      }
    }

    setWithoutTowerExist(hasEmptyTower);
  }, [selectedItems]);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setName('');
      setLastname('');
      setPhone('');
      setNote('');
      setCode('');
      setSelectedOption('manuel');
      setSelectedPersonel('');
      setSelectedPorter('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (selectedOption === 'auto' && personelList.length === 0) {
      setIsBackdropOpen(true);

      const metaTagsArr = [];
      for (let i = 0; i < selectedItems.length; i++) {
        if (selectedItems[i]?.dataJSON?.tower) {
          metaTagsArr.push(selectedItems[i].dataJSON.tower);
        }
      }

      api
        .post('flow/concierge-get-users', {
          metaTags: metaTagsArr,
        })
        .then((response) => {
          if (response.data.status) {
            setPersonelList(response.data.result || []);
          } else {
            toast.error('Kullanıcı bulunamadı');
            setPersonelList([]);
          }
        })
        .catch((error) => {
          toast.error(error?.message || 'Bir hata oluştu');
          setPersonelList([]);
        })
        .finally(() => {
          setIsBackdropOpen(false);
        });
    } else if (selectedOption === 'porter' && porterList.length === 0) {
      setIsBackdropOpen(true);
      const mallID = localStorage.getItem('mallID');

      api
        .post('users/get-users', {
          type: 'porter',
          mallID,
          status: '1',
        })
        .then((response) => {
          if (response.data.status) {
            const filteredPorters = (response.data.result || []).filter(
              (porter) =>
                Array.isArray(porter.frontAuth) &&
                porter.frontAuth.includes('muhaberat-porter')
            );
            setPorterList(filteredPorters);
          } else {
            toast.error('Kullanıcı bulunamadı');
            setPorterList([]);
          }
        })
        .catch((error) => {
          toast.error(error?.message || 'Bir hata oluştu');
          setPorterList([]);
        })
        .finally(() => {
          setIsBackdropOpen(false);
        });
    }
  }, [
    open,
    selectedOption,
    personelList.length,
    porterList.length,
    selectedItems,
  ]);

  const handlePhoneNumberChange = (event) => {
    const inputValue = event.target.value;
    const numericValue = inputValue.replace(/[^0-9]/g, '');
    setPhone(numericValue);
  };

  const handleOptionChange = (event) => {
    setName('');
    setLastname('');
    setPhone('');
    setNote('');
    setSelectedPersonel('');
    setSelectedPorter('');
    setCode('');
    setStep(1);
    setSelectedOption(event.target.value);
  };

  const handlePersonelChange = (event) => {
    const value = event.target.value;
    setSelectedPersonel(value);

    const currentSelectedPersonel = personelList.find(
      (personel) => personel.userID === value
    );

    setName(currentSelectedPersonel?.name || '');
    setLastname(currentSelectedPersonel?.lastname || '');
    setPhone(currentSelectedPersonel?.phone || '');
  };

  const handlePorterChange = (event) => {
    const value = event.target.value;
    setSelectedPorter(value);

    const currentSelectedPorter = porterList.find(
      (porter) => porter.id === value
    );

    setName(currentSelectedPorter?.name || '');
    setLastname(currentSelectedPorter?.lastname || '');
    setPhone(currentSelectedPorter?.phone || '');
  };

  const maskPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    const maskedPart = phoneNumber.slice(0, -4).replace(/\d/g, '*');
    return maskedPart + phoneNumber.slice(-4);
  };

  const canContinueStep1 = useMemo(() => {
    if (selectedOption === 'manuel') {
      return name?.length >= 2 && lastname?.length >= 2 && phone?.length === 10;
    }

    if (selectedOption === 'porter') {
      return (
        !!selectedPorter &&
        name?.length >= 2 &&
        lastname?.length >= 2 &&
        (phone?.length === 10 || phone?.length === 11)
      );
    }

    return (
      name?.length >= 2 &&
      lastname?.length >= 2 &&
      (phone?.length === 10 || phone?.length === 11)
    );
  }, [selectedOption, selectedPorter, name, lastname, phone]);

  const handleNext = async () => {
    if (step === 1) {
      if (isPorterFlow) {
        const currentSelectedPorter = porterList.find(
          (porter) => porter.id === selectedPorter
        );

        const result = await onDeliver(
          name,
          lastname,
          phone,
          note,
          'porter',
          currentSelectedPorter || null
        );

        if (result) {
          setStep(2);
        }
        return;
      }

      const result = await onDeliver(
        name,
        lastname,
        phone,
        note,
        selectedOption,
        null
      );

      if (result !== false) {
        setStep(2);
      }
      return;
    }

    onComplete(code, selectedOption, null);
  };

  const handleBack = () => {
    if (step === 1) {
      onClose();
    } else if (step === 2) {
      setStep(1);
    } else {
      onBack();
    }
  };

  const handlePorterDoneClick = () => {
    onPorterDone?.();
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <Backdrop
        sx={{ color: '#fff', zIndex: 9999 }}
        open={isBackdropOpen || isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <DialogTitle>
        {step === 1
          ? 'Adres Bilgileri'
          : isPorterFlow
          ? 'Muhaberat QR'
          : 'Onay Kodu'}
      </DialogTitle>

      <DialogContent>
        {step === 1 ? (
          <div>
            <Grid container spacing={2}>
              <Grid item xs={showPorterOption ? 4 : 6}>
                <FormControl component="fieldset">
                  <RadioGroup
                    aria-label="option"
                    name="option"
                    value={selectedOption}
                    onChange={handleOptionChange}
                  >
                    <FormControlLabel
                      value="manuel"
                      control={<Radio />}
                      label="Bilgileri elle gir"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {showPorterOption && (
                <Grid item xs={withoutTowerExist ? 8 : 4}>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="option"
                      name="option"
                      value={selectedOption}
                      onChange={handleOptionChange}
                    >
                      <FormControlLabel
                        value="porter"
                        control={<Radio />}
                        label="Porter Seçerek İlerle"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}

              {!withoutTowerExist && (
                <Grid item xs={showPorterOption ? 4 : 6}>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="option"
                      name="option"
                      value={selectedOption}
                      onChange={handleOptionChange}
                    >
                      <FormControlLabel
                        value="auto"
                        control={<Radio />}
                        label="Bilgileri sistemden seç"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}
            </Grid>

            {selectedOption === 'manuel' ? (
              <>
                <TextField
                  fullWidth
                  label="Ad"
                  variant="outlined"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  sx={{ mb: 2, mt: 2 }}
                />

                <TextField
                  fullWidth
                  label="Soyad"
                  variant="outlined"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Telefon"
                  placeholder="5XXXXXXXXX"
                  variant="outlined"
                  inputProps={{ maxLength: 10 }}
                  value={phone}
                  onChange={handlePhoneNumberChange}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Notlar"
                  variant="outlined"
                  multiline
                  rows={4}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </>
            ) : selectedOption === 'auto' ? (
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Personel</InputLabel>
                  <Select
                    sx={{ width: '100%' }}
                    label="Personel"
                    value={selectedPersonel}
                    onChange={handlePersonelChange}
                    name="personel"
                    disabled={personelList.length === 0}
                  >
                    {personelList.map((personel, index) => (
                      <MenuItem key={index} value={personel.userID}>
                        {personel.name} {personel.lastname}
                      </MenuItem>
                    ))}
                  </Select>
                  {porterList.length === 0 && !isBackdropOpen && (
                    <Typography color="error" sx={{ mt: 1, mb: 1 }}>
                      Muhaberat yetkisi olan porter bulunamadı.
                    </Typography>
                  )}
                </FormControl>

                <TextField
                  fullWidth
                  label="Telefon"
                  variant="outlined"
                  placeholder="5XXXXXXXXX"
                  value={maskPhoneNumber(phone)}
                  disabled
                  sx={{ mb: 2, mt: 1 }}
                />

                <TextField
                  fullWidth
                  label="Notlar"
                  variant="outlined"
                  multiline
                  rows={4}
                  value={note}
                  disabled={personelList.length === 0}
                  onChange={(e) => setNote(e.target.value)}
                  sx={{ mb: 2 }}
                />

                {personelList.length === 0 && !isBackdropOpen ? (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Iconify icon="eva:plus-fill" />}
                      onClick={onAddPersonel}
                    >
                      Kule Çalışanı Ekle
                    </Button>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Porter</InputLabel>
                  <Select
                    sx={{ width: '100%' }}
                    label="Porter"
                    value={selectedPorter}
                    onChange={handlePorterChange}
                    name="porter"
                    disabled={porterList.length === 0}
                  >
                    {porterList.map((porter, index) => (
                      <MenuItem key={index} value={porter.id}>
                        {porter.name} {porter.lastname}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Telefon"
                  variant="outlined"
                  placeholder="5XXXXXXXXX"
                  value={maskPhoneNumber(phone)}
                  disabled
                  sx={{ mb: 2, mt: 1 }}
                />

                <TextField
                  fullWidth
                  label="Notlar"
                  variant="outlined"
                  multiline
                  rows={4}
                  value={note}
                  disabled={porterList.length === 0}
                  onChange={(e) => setNote(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </div>
        ) : isPorterFlow ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 2,
            }}
          >
            <DialogContentText sx={{ textAlign: 'center', mb: 2 }}>
              Seçilen porter ile teslim işlemini tamamlamak için aşağıdaki
              Muhaberat QR görselini gösteriniz.
            </DialogContentText>

            {muhaberatStaticQR ? (
              <Box
                sx={{
                  width: 260,
                  height: 260,
                  border: '1px solid #E0E0E0',
                  borderRadius: 2,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fff',
                  p: 1,
                }}
              >
                <img
                  src={muhaberatStaticQR}
                  alt="Muhaberat QR"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            ) : (
              <Typography color="error" sx={{ mt: 1 }}>
                Muhaberat QR bulunamadı.
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <DialogContentText>
              <span style={{ fontWeight: '700', color: 'black' }}>{count}</span>{' '}
              adet gönderinin <b>({packageCount} paket)</b> teslimi için {phone}{' '}
              numaralı telefona gönderilen onay kodunu giriniz
            </DialogContentText>

            <TextField
              fullWidth
              label="Kodu girin"
              variant="outlined"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              sx={{ mt: 2 }}
            />
          </>
        )}
      </DialogContent>

      {step === 2 && isPorterFlow ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pb: 3,
            px: 3,
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handlePorterDoneClick}
            disabled={!muhaberatStaticQR}
            sx={{ minWidth: 180 }}
          >
            Tamam
          </Button>

          <Button
            variant="text"
            color="inherit"
            onClick={async () => {
              await onCancelPorter?.();
              setStep(1);
              setSelectedPorter('');
              setName('');
              setLastname('');
              setPhone('');
              setNote('');
            }}
            sx={{ minWidth: 180 }}
          >
            İptal Et
          </Button>
        </Box>
      ) : (
        <DialogActions>
          <Button onClick={handleBack} color="primary">
            Geri
          </Button>

          {step === 1 ? (
            <Button
              disabled={!canContinueStep1}
              onClick={handleNext}
              color="primary"
            >
              İlerle
            </Button>
          ) : (
            <Button disabled={!code} onClick={handleNext} color="primary">
              Teslim Et
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default DeliveryModal;
